// src/components/ui/emptyState/EmptyState.tsx
'use client';

import React from 'react';
import styles from './EmptyState.module.scss';

type Size = 'sm' | 'md' | 'lg';
type Align = 'center' | 'left';
type Tone = 'muted' | 'neutral' | 'brand' | 'danger';

function cn(...args: Array<string | false | null | undefined>) {
  return args.filter(Boolean).join(' ');
}

export type EmptyStateProps = {
  /** 是否顯示；方便 JSX 直接掛上 */
  show?: boolean;

  /** Icon：可直接傳 <IconNote /> 或任何 ReactNode */
  icon?: React.ReactNode;

  /** 主標題（建議必填） */
  title: React.ReactNode;

  /** 補充說明（可不填） */
  description?: React.ReactNode;

  /** 行動區（放 Button / Link 等） */
  action?: React.ReactNode;

  /** 版型 */
  align?: Align;
  size?: Size;

  /** 色調 */
  tone?: Tone;

  /** 最小高度（例如列表區想撐高） */
  minHeight?: number | string;

  /** 外層 className（給各頁面覆寫 spacing / utility class） */
  className?: string;

  /** 各區塊額外 className（更細緻客製用） */
  iconClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  actionClassName?: string;

  /** A11y：資料載入後顯示空狀態時可讓 SR 讀到 */
  ariaLive?: 'off' | 'polite' | 'assertive';

  'data-testid'?: string;
};

export default function EmptyState({
  show = true,
  icon,
  title,
  description,
  action,
  align = 'center',
  size = 'md',
  tone = 'muted',
  minHeight,
  className,
  iconClassName,
  titleClassName,
  descriptionClassName,
  actionClassName,
  ariaLive = 'polite',
  'data-testid': dataTestId,
}: EmptyStateProps) {
  if (!show) return null;

  const styleVar: React.CSSProperties | undefined =
    minHeight !== undefined ? { minHeight } : undefined;

  return (
    <section
      className={cn(
        styles.root,
        styles[`align-${align}`],
        styles[`size-${size}`],
        styles[`tone-${tone}`],
        className
      )}
      style={styleVar}
      role="status"
      aria-live={ariaLive}
      data-testid={dataTestId}
    >
      <div className={styles.inner}>
        {icon ? <div className={cn(styles.icon, iconClassName)}>{icon}</div> : null}

        <div className={styles.text}>
          <div className={cn(styles.title, titleClassName)}>{title}</div>

          {description ? (
            <div className={cn(styles.description, descriptionClassName)}>{description}</div>
          ) : null}

          {action ? <div className={cn(styles.action, actionClassName)}>{action}</div> : null}
        </div>
      </div>
    </section>
  );
}
